import type { ParagraphPricingTiers as ParagraphPricingTiersData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphPricingTiers.module.css";

/**
 * Layout 41 - `PARAGRAPH_PRICING_TIERS_LAYOUT`: pricing tiers.
 *
 * Struktura:
 *   - Eyebrow (text_1) - opcjonalny uppercase nad tytulem sekcji
 *   - H2 (name) - tytul sekcji
 *   - Lead (text) - paragraf pod tytulem (HTML)
 *   - Grid do 3 kart pakietow (puste sloty filtrowane):
 *       - Featured (opcjonalna) - green badge nad karta + wypelniony green CTA
 *       - Eyebrow (uppercase, opcjonalny) + title (h3 bold) + tagline
 *       - price (big) + price_suffix (small) + price_secondary (mała linia)
 *       - subtitle (accent color pod cenami, np. "wdrozenie w 4-6 tygodni")
 *       - Checklist cech (kazda z zielonym checkmarkiem)
 *       - CTA (green filled dla featured / outlined dla non-featured)
 *   - Footnote pod kartami (plain text)
 */
export function ParagraphPricingTiers({
  paragraph,
}: {
  paragraph: ParagraphPricingTiersData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: lead, cards, footnote } = paragraph;
  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasFootnote = footnote && footnote.trim() !== "";

  //Filter cards - display only ones with at least title/eyebrow OR price OR features
  const visibleCards = (cards ?? []).filter((c) =>
    Boolean(c.title || c.eyebrow || c.price || c.subtitle || (c.features && c.features.length > 0))
  );

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        {hasEyebrow ? <p className={styles.sectionEyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasLead ? (
          <HtmlText html={lead} className={styles.lead} />
        ) : null}
      </div>

      {visibleCards.length > 0 ? (
        <div className={styles.grid} data-count={visibleCards.length}>
          {visibleCards.map((card, idx) => {
            const hasCta = card.cta_label && card.cta_url;
            return (
              <div
                key={idx}
                className={`${styles.card} ${card.featured ? styles.cardFeatured : ""}`}
              >
                {card.featured && card.featured_label ? (
                  <div className={styles.badge}>{card.featured_label}</div>
                ) : null}
                <div className={styles.cardBody}>
                  {card.eyebrow ? <p className={styles.eyebrow}>{card.eyebrow}</p> : null}
                  {card.title ? <h3 className={styles.cardTitle}>{card.title}</h3> : null}
                  {card.tagline ? <p className={styles.tagline}>{card.tagline}</p> : null}
                  {(card.price || card.price_suffix) ? (
                    <p className={styles.priceRow}>
                      {card.price ? <span className={styles.price}>{card.price}</span> : null}
                      {card.price_suffix ? (
                        <span className={styles.priceSuffix}>{card.price_suffix}</span>
                      ) : null}
                    </p>
                  ) : null}
                  {card.price_secondary ? (
                    <p className={styles.priceSecondary}>{card.price_secondary}</p>
                  ) : null}
                  {card.subtitle ? <p className={styles.subtitle}>{card.subtitle}</p> : null}

                  {card.features && card.features.length > 0 ? (
                    <ul className={styles.features}>
                      {card.features.map((feat, i) => (
                        <li key={i} className={styles.feature}>
                          <span className={styles.check} aria-hidden="true" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {hasCta ? (
                    <a href={card.cta_url} className={styles.cta}>
                      {card.cta_label}
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {hasFootnote ? <p className={styles.footnote}>{footnote}</p> : null}
    </section>
  );
}
